import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCog, ChevronRight, CheckCircle2, ClipboardCheck, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [{ v: "week", l: "Settimana" }, { v: "month", l: "Mese" }, { v: "season", l: "Stagione" }];
function toISO(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function period(scope) {
  const end = new Date(); const start = new Date();
  if (scope === "week") start.setDate(end.getDate() - end.getDay() + 1);
  else if (scope === "season") { start.setMonth(7, 1); if (start > end) start.setFullYear(start.getFullYear() - 1); }
  else start.setDate(1);
  return { from: toISO(start), to: toISO(end) };
}

export default function StaffControl() {
  const [scope, setScope] = useState("month");
  const [groupId, setGroupId] = useState("all");
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [showProfiles, setShowProfiles] = useState(false);

  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {}); }, []);
  const loadProfiles = useCallback(() => { api.get("/technical/staff-profiles").then((r) => setProfiles(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = period(scope);
    const params = { ...p };
    if (groupId !== "all") params.group_id = groupId;
    try {
      const { data } = await api.get("/technical/staff-control", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [scope, groupId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="staff-control">
      <div className="flex items-end gap-3 flex-wrap">
        <FF label="Periodo"><Select value={scope} onValueChange={setScope}><SelectTrigger data-testid="sc-period" className="h-10 w-40"><SelectValue /></SelectTrigger><SelectContent>{PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select></FF>
        <FF label="Gruppo"><Select value={groupId} onValueChange={setGroupId}><SelectTrigger data-testid="sc-group" className="h-10 w-56"><SelectValue /></SelectTrigger><SelectContent className="max-h-72"><SelectItem value="all">Tutti i gruppi</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></FF>
      </div>

      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Svolto</span>
        <span className="text-zinc-300">≠</span>
        <span className="inline-flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5 text-[#0052FF]" /> Registrato</span>
        <span className="ml-2">· l'assenza di registrazione non significa lavoro non svolto</span>
      </div>

      {loading ? <div className="text-zinc-500 py-12 text-center">Caricamento…</div> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center text-zinc-500" data-testid="sc-empty">Nessun lavoro tecnico assegnato/registrato nel periodo.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => (
            <button key={c.staff_id} data-testid={`sc-card-${c.staff_id}`} onClick={() => setSel(c.staff_id)}
              className="bg-white border border-zinc-200 rounded-md p-4 text-left hover:border-zinc-400 transition">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-xs font-semibold shrink-0">{(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold truncate flex items-center gap-1">{c.name}{c.is_technical_director && <ShieldCheck className="w-3.5 h-3.5 text-[#0052FF]" title="Direzione Tecnica" />}</div>
                    {(c.responsibilities || []).length > 0 && <div className="text-[11px] text-zinc-500 truncate">{c.responsibilities.join(" · ")}</div>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-3">
                <Kpi label="Assegn." value={c.assigned} />
                <Kpi label="Svolto" value={c.done} tone="emerald" />
                <Kpi label="Registr." value={c.registered} tone="blue" />
                <Kpi label="Mancanti" value={c.missing} tone={c.missing > 0 ? "amber" : "zinc"} />
              </div>
              <div className="text-[11px] text-zinc-500 mt-2 font-mono">Oggi {c.planned_today} · Settimana {c.planned_week} · {c.effective_minutes}′</div>
            </button>
          ))}
        </div>
      )}

      {/* Profili tecnici (engine data · leggero) */}
      <div>
        <button data-testid="sc-toggle-profiles" onClick={() => setShowProfiles((v) => !v)} className="text-xs uppercase tracking-wider text-[#0052FF] flex items-center gap-1"><UserCog className="w-3.5 h-3.5" /> {showProfiles ? "Nascondi" : "Profili tecnici"}</button>
        {showProfiles && (
          <div className="mt-3 space-y-2" data-testid="sc-profiles">
            {profiles.map((p) => (
              <div key={p.id} data-testid={`profile-${p.id}`} className="bg-white border border-zinc-200 rounded-md p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{p.name}</span>
                  {p.is_technical_director && <span className="text-[10px] uppercase tracking-wider bg-[#0052FF] text-white rounded px-1.5 py-0.5">Direzione Tecnica</span>}
                  {(p.responsibilities || []).map((r) => <span key={r} className="text-[10px] uppercase tracking-wider bg-zinc-900 text-white rounded px-1.5 py-0.5">{r}</span>)}
                </div>
                <div className="text-xs text-zinc-500 mt-1">Capacità: {(p.capabilities || []).join(", ") || "—"} · Prevalenza: {(p.preferred_assignments || []).join(", ") || "—"}</div>
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Gruppi assegnati (modificabili)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {groups.map((g) => {
                      const on = (p.group_assignments || []).includes(g.id);
                      return <button key={g.id} data-testid={`profile-${p.id}-group-${g.id}`}
                        onClick={async () => {
                          const next = on ? p.group_assignments.filter((x) => x !== g.id) : [...(p.group_assignments || []), g.id];
                          try { await api.put(`/technical/staff-profiles/${p.id}`, { group_assignments: next }); loadProfiles(); toast.success("Assegnazione aggiornata"); } catch { toast.error("Errore"); }
                        }}
                        className={`text-[11px] px-2 py-1 rounded-full border ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>{g.name}</button>;
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {sel && <HistoryModal staffId={sel} scope={scope} groupId={groupId} onClose={() => setSel(null)} />}
    </div>
  );
}

function HistoryModal({ staffId, scope, groupId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const params = { scope }; if (groupId !== "all") params.group_id = groupId;
    api.get(`/technical/staff/${staffId}/history`, { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [staffId, scope, groupId]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="sc-history">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        {loading ? <div className="py-12 text-center text-zinc-500">Caricamento…</div> : data ? (
          <>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-heading text-xl font-bold">{data.staff?.name}</h2>
                <div className="text-sm text-zinc-500">{data.period?.from} → {data.period?.to}</div>
              </div>
              <button onClick={onClose} data-testid="sc-history-close" className="text-sm text-zinc-500 hover:text-black">Chiudi</button>
            </div>
            {data.profile && (
              <div className="text-xs text-zinc-600 mb-3 flex flex-wrap gap-2">
                {(data.profile.capabilities || []).map((c) => <span key={c} className="bg-zinc-100 rounded px-2 py-0.5">{c}</span>)}
              </div>
            )}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
              <Kpi label="Svolte" value={data.summary.sessions_done} tone="emerald" />
              <Kpi label="Registrate" value={data.summary.registered} tone="blue" />
              <Kpi label="Incompl." value={data.summary.incomplete} tone={data.summary.incomplete > 0 ? "amber" : "zinc"} />
              <Kpi label="Minuti" value={`${data.summary.effective_minutes}′`} />
              <Kpi label="Lezioni" value={data.summary.lessons} />
              <Kpi label="Atleti" value={data.summary.athletes_reached} />
            </div>
            <div className="text-xs text-zinc-500 mb-3">Gruppi: {(data.summary.groups || []).join(", ") || "—"} · Attività: {data.summary.activities}</div>
            <h3 className="font-semibold mb-2">Timeline</h3>
            {data.timeline.length === 0 ? <div className="text-sm text-zinc-400">Nessuna attività nel periodo.</div> : (
              <div className="space-y-1.5">
                {data.timeline.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-100 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-zinc-400 w-20 shrink-0">{e.date}</span>
                      <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${e.type === "lezione" ? "bg-violet-50 text-violet-700" : "bg-zinc-100 text-zinc-600"}`}>{e.type}</span>
                      <span className="truncate">{e.label}{e.activities?.length ? ` · ${e.activities.join(", ")}` : ""}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono text-xs text-zinc-500">{e.minutes}′</span>
                      {e.type === "seduta" && (e.registered ? <ClipboardCheck className="w-3.5 h-3.5 text-[#0052FF]" title="Registrato" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Non registrato" />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : <div className="text-sm text-red-600">Errore caricamento.</div>}
      </div>
    </div>
  );
}

function FF({ label, children }) { return <div className="flex flex-col"><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>{children}</div>; }
function Kpi({ label, value, tone = "zinc" }) {
  const tones = { zinc: "text-zinc-900", emerald: "text-emerald-700", blue: "text-[#0052FF]", amber: "text-amber-600" };
  return <div className="bg-zinc-50 border border-zinc-200 rounded p-2 text-center"><div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div><div className={`text-base font-bold ${tones[tone]}`}>{value}</div></div>;
}
